import { useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { Sparkles } from "@react-three/drei"
import * as THREE from "three"

import { modelPalette } from "./modelPalette"
import type { AvatarModel, AvatarPolarity } from "@domo/shared"

// 2026-05-06 — Animación de transmutación al pisar el portal de
// Reconfiguración. El cuerpo se concentra en una bola de luz dorada
// (color firma del avatar) que se eleva del piso al cielo durante
// `durationMs`. Al final el componente padre (cliente del Domo) emite
// RECONFIGURE_REQUEST al server; el server limpia la firma y devuelve
// VOID_RETURN, que lleva la fase a "void" para elegir nueva firma.
//
// Visualmente:
//   · 0–25 % (entrada): el orbe nace en y=1 (centro del cuerpo) con
//     un fade-in rápido + scale 0 → 1.
//   · 25–80 % (ascenso): el orbe sube de y=1 a y=ASCENT_TOP siguiendo
//     una curva ease-out (rápido al inicio, suave al final). Sparkles
//     densas al rededor lo acompañan.
//   · 80–100 % (disolución): fade-out del orbe + scale leve.
//
// La paleta usa `modelPalette` con la firma actual del Tripulante
// para que el color del orbe encaje con la energía de la encarnación
// que está dejando atrás (continuidad visual entre la encarnación y
// el rito de cambio).

const ASCENT_TOP = 18
const ORB_BASE_Y = 1.1
const ORB_RADIUS = 0.55

interface AscentOrbProps {
    /** Posición XZ del avatar en el momento del despegue. */
    pad: [number, number, number]
    model: AvatarModel
    polarity: AvatarPolarity
    /** performance.now() del comienzo de la animación. */
    startedAt: number
    /** Duración total en ms. */
    durationMs: number
}

export function AscentOrb({
    pad,
    model,
    polarity,
    startedAt,
    durationMs,
}: AscentOrbProps) {
    const orbRef = useRef<THREE.Mesh>(null)
    const haloRef = useRef<THREE.Mesh>(null)
    const matRef = useRef<THREE.MeshBasicMaterial>(null)
    const haloMatRef = useRef<THREE.MeshBasicMaterial>(null)
    const lightRef = useRef<THREE.PointLight>(null)

    const palette = useMemo(
        () => modelPalette(model, polarity),
        [model, polarity]
    )

    useFrame(() => {
        const elapsed = performance.now() - startedAt
        const t = Math.max(0, Math.min(1, elapsed / Math.max(1, durationMs)))

        // Curva de altura: ease-out cubic (rápido al despegar, suave
        // al llegar al cielo). Nace en ORB_BASE_Y, llega a ASCENT_TOP.
        const easeOut = 1 - Math.pow(1 - t, 3)
        const y = ORB_BASE_Y + easeOut * (ASCENT_TOP - ORB_BASE_Y)

        // Fade-in en 0–25 %, fade-out en 80–100 %, full opacity en
        // medio.
        let alpha = 1
        if (t < 0.25) alpha = t / 0.25
        else if (t > 0.8) alpha = 1 - (t - 0.8) / 0.2
        alpha = Math.max(0, Math.min(1, alpha))

        // Pulso sutil del scale del orbe (respiración energética).
        const pulse = 1 + Math.sin(elapsed * 0.012) * 0.08

        if (orbRef.current) {
            orbRef.current.position.set(pad[0], y, pad[2])
            orbRef.current.scale.setScalar(pulse)
        }
        if (haloRef.current) {
            haloRef.current.position.set(pad[0], y, pad[2])
            haloRef.current.scale.setScalar(1.6 + Math.sin(elapsed * 0.008) * 0.18)
        }
        if (matRef.current) {
            matRef.current.opacity = 0.92 * alpha
        }
        if (haloMatRef.current) {
            haloMatRef.current.opacity = 0.32 * alpha
        }
        if (lightRef.current) {
            lightRef.current.position.set(pad[0], y, pad[2])
            lightRef.current.intensity = 2.6 * alpha
        }
    })

    return (
        <group>
            {/* Núcleo brillante: bola del color de la firma. */}
            <mesh ref={orbRef} renderOrder={6}>
                <sphereGeometry args={[ORB_RADIUS, 32, 32]} />
                <meshBasicMaterial
                    ref={matRef}
                    color={palette.avatarCore}
                    transparent
                    opacity={0}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </mesh>
            {/* Halo difuso envolvente. */}
            <mesh ref={haloRef} renderOrder={5}>
                <sphereGeometry args={[ORB_RADIUS * 1.6, 24, 24]} />
                <meshBasicMaterial
                    ref={haloMatRef}
                    color={palette.avatarHalo}
                    transparent
                    opacity={0}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </mesh>
            {/* Sparkles densas que ascienden con el orbe — refuerzan
                la sensación de "consciencia liberándose". */}
            <Sparkles
                count={36}
                scale={[1.6, 3.5, 1.6]}
                position={[
                    pad[0],
                    ORB_BASE_Y +
                        ((ASCENT_TOP - ORB_BASE_Y) *
                            Math.max(
                                0,
                                Math.min(
                                    1,
                                    (performance.now() - startedAt) /
                                        Math.max(1, durationMs)
                                )
                            )),
                    pad[2],
                ]}
                size={5}
                speed={0.8}
                color={palette.avatarHalo}
                opacity={0.85}
            />
            {/* Punto de luz que viaja con el orbe — proyecta color
                cálido sobre el piso y los aros cercanos. */}
            <pointLight
                ref={lightRef}
                color={palette.avatarHalo}
                intensity={2.6}
                distance={8}
                decay={1.6}
            />
        </group>
    )
}
